/**
 * Tax and compliance — `/v1/admin/tax/*`.
 *
 * **Nothing here holds a copy of a rate, a threshold or a flag.** Which rates are live and
 * unsigned, what a period nets to, how much is still un-deposited and whether Section 52 TCS is
 * even switched on all come back from the server. A client copy of any of them is a second
 * answer to a question the server already answers, and this console has been wrong that way
 * three times.
 *
 * **The return export is text, not JSON.** It is the CSV a CA files from, so it takes the
 * client's `parse: 'text'` path — parsing it as JSON threw `malformed_response` on a perfectly
 * correct response.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { idempotent } from '../idempotency';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type TaxRateRow = components['schemas']['TaxRateRow'];
export type TaxRateList = components['schemas']['TaxRateListResponse'];
export type TdsRecordRow = components['schemas']['TdsRecordRow'];
export type TdsRegister = components['schemas']['TdsRegisterResponse'];
export type ChallanRow = components['schemas']['ChallanRow'];
export type TdsFilingRow = components['schemas']['TdsFilingRow'];
export type TaxApprovalRow = components['schemas']['TaxApprovalRow'];
export type TaxApprovalList = components['schemas']['TaxApprovalList'];
export type TaxApprovalDecision = components['schemas']['TaxApprovalDecision'];
export type CertificateBatch = components['schemas']['CertificateBatchResponse'];
export type GstRegister = components['schemas']['GstRegisterResponse'];
export type GstRecordRow = components['schemas']['GstRecordRow'];
export type PanState = components['schemas']['PanStateOut'];
export type PanRow = components['schemas']['PanRow'];

export interface RegisterQuery extends Record<string, unknown> {
  fy: string;
  quarter?: string;
  undeposited?: boolean;
  page: number;
  page_size: number;
}

/** Exported for its own test: an absent filter and an explicit one are different requests. */
export function registerSearch(params: RegisterQuery): string {
  const search = new URLSearchParams({
    fy: params.fy,
    page: String(params.page),
    page_size: String(params.page_size),
  });
  if (params.quarter !== undefined) search.set('quarter', params.quarter);
  if (params.undeposited) search.set('undeposited', 'true');
  return search.toString();
}

export function useTaxRates() {
  return useQuery({
    queryKey: keys.tax.rates(),
    queryFn: () => authedClient.request<TaxRateList>('/v1/admin/tax/rates'),
  });
}

export function useTdsRegister(params: RegisterQuery) {
  return useQuery({
    queryKey: keys.tax.tdsRegister(params),
    queryFn: () =>
      authedClient.request<TdsRegister>(`/v1/admin/tax/tds/register?${registerSearch(params)}`),
  });
}

/**
 * `enabled` gates these on the open tab.
 *
 * Reading the deposits and the GST register for somebody looking at the rate registry is three
 * requests nobody asked for — and it means the answer has always arrived before the tab is
 * opened, so the loading state a slow link would show is one nothing can reach.
 */
export function useChallans(fy: string, enabled: boolean) {
  return useQuery({
    queryKey: keys.tax.challans(fy),
    queryFn: () => authedClient.request<ChallanRow[]>(`/v1/admin/tax/tds/challans?fy=${fy}`),
    enabled,
  });
}

/** The quarterly returns recorded as filed — four quarters and their corrections, so not paged. */
export function useTdsFilings(fy: string, enabled: boolean) {
  return useQuery({
    queryKey: keys.tax.filings(fy),
    queryFn: () => authedClient.request<TdsFilingRow[]>(`/v1/admin/tax/tds/filings?fy=${fy}`),
    enabled,
  });
}

/** Section 52 has its own register because it has its own return — GSTR-8 rather than GSTR-1. */
export function useGstRegister(period: string, tcs: boolean, enabled: boolean) {
  return useQuery({
    queryKey: keys.tax.gstRegister({ period, tcs }),
    queryFn: () =>
      authedClient.request<GstRegister>(
        `/v1/admin/tax/gst/${tcs ? 'tcs-register' : 'register'}?period=${period}`,
      ),
    enabled,
  });
}

/**
 * Coarse invalidation on purpose. Recording a challan changes the register, the challan list and
 * the un-deposited total at once; a hand-maintained map of which keys each action touches goes
 * stale the first time somebody adds a filter.
 */
function useTaxMutation<TArgs, TData>(fn: (args: TArgs) => Promise<TData>) {
  const client = useQueryClient();
  return useMutation<TData, unknown, TArgs>({
    mutationFn: fn,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.tax.all() });
    },
  });
}

/**
 * The four-eyes queue (backend 29 §4.3). A rate, a challan and a remittance each park here and run
 * only when a different finance admin approves; the server refuses the requester's own approval.
 */
export function useTaxApprovals(status: 'pending' | 'applied' | 'rejected') {
  return useQuery({
    queryKey: keys.tax.approvals(status),
    queryFn: () => authedClient.request<TaxApprovalList>(`/v1/admin/tax/approvals?status=${status}`),
  });
}

export function useDecideTaxApproval() {
  return useTaxMutation(
    ({ id, decision, note }: { id: number; decision: 'approve' | 'reject'; note: string }) =>
      authedClient.request<TaxApprovalDecision>(`/v1/admin/tax/approvals/${id}/${decision}`, {
        method: 'POST',
        body: { note },
      }),
  );
}

/**
 * `basis` is the CA's ruling on what the rate is charged on (R211). Left out, the server carries
 * the latest version's over, and refuses 422 `basis_required` when there is none to inherit.
 */
export function useAddRate() {
  return useTaxMutation(
    (body: {
      rate_type: string;
      rate: string;
      basis?: string;
      effective_from: string;
      effective_to?: string | null;
      source_note: string;
      reason: string;
    }) => authedClient.request<TaxApprovalRow>('/v1/admin/tax/rates', { method: 'POST', body }),
  );
}

/**
 * The CA's reference is appended to the source note, never replacing where the number came from.
 * Parked for a second admin like every tax write (backend 29 §8); the rate stays unconfirmed until
 * the approval runs.
 */
export function useConfirmRate() {
  return useTaxMutation(
    ({ rateId, sourceNote, reason }: { rateId: number; sourceNote: string; reason: string }) =>
      authedClient.request<TaxApprovalRow>(`/v1/admin/tax/rates/${rateId}/confirm`, {
        method: 'POST',
        body: { source_note: sourceNote, reason },
      }),
  );
}

/** Where a provider's PAN stands — `none`, `unverified`, `verified` or `mismatch`; never the number. */
export function usePanState(panditProfileId: number) {
  return useQuery({
    queryKey: keys.tax.pan(panditProfileId),
    queryFn: () => authedClient.request<PanState>(`/v1/admin/tax/pan/${panditProfileId}`),
  });
}

/** A person's verdict on the card: the only path off the Section 206AA higher rate. */
export function useVerifyPan() {
  return useTaxMutation(
    ({ panditProfileId, matched, reason }: { panditProfileId: number; matched: boolean; reason: string }) =>
      authedClient.request<PanRow>(`/v1/admin/tax/pan/${panditProfileId}/verify`, {
        method: 'POST',
        body: { matched, reason },
      }),
  );
}

/**
 * Record a return already filed on the portal. No second approver — it moves no money — but the
 * server refuses a quarter while any deduction in it has no challan (08-payments/11 §3).
 */
export function useRecordTdsFiling() {
  return useTaxMutation(
    ({
      key,
      ...body
    }: {
      key: string;
      financial_year: string;
      quarter: string;
      acknowledgement_no: string;
      filed_on: string;
      is_correction: boolean;
      reason: string;
    }) =>
      authedClient.request<TdsFilingRow>('/v1/admin/tax/tds/filings', {
        method: 'POST',
        body,
        headers: idempotent(key),
      }),
  );
}

export function useRecordChallan() {
  return useTaxMutation(
    ({
      key,
      ...body
    }: {
      key: string;
      challan_number: string;
      bsr_code: string | null;
      bank_reference: string | null;
      deposit_date: string;
      period_month: string;
      financial_year: string;
      total_paise: number;
      allocations: { tds_record_id: number; amount_paise: number }[];
      reason: string;
    }) =>
      authedClient.request<TaxApprovalRow>('/v1/admin/tax/tds/challans', {
        method: 'POST',
        body,
        headers: idempotent(key),
      }),
  );
}

export function useGenerateCertificates() {
  return useTaxMutation(({ fy, quarter, key }: { fy: string; quarter: string; key: string }) =>
    authedClient.request<CertificateBatch>(
      `/v1/admin/tax/tds/certificates/generate?fy=${fy}&quarter=${quarter}`,
      { method: 'POST', headers: idempotent(key) },
    ),
  );
}

export function useRecordGstRemittance() {
  return useTaxMutation(
    ({
      key,
      tcs,
      ...body
    }: {
      key: string;
      tcs: boolean;
      return_period: string;
      amount_paise: number;
      reference: string;
      remitted_on: string;
      reason: string;
    }) =>
      authedClient.request<TaxApprovalRow>(`/v1/admin/tax/gst/remittance?tcs=${tcs}`, {
        method: 'POST',
        body,
        headers: idempotent(key),
      }),
  );
}

/**
 * The quarterly return, as the file a CA files from.
 *
 * A mutation rather than a query: it is a download somebody asks for, and caching it would hand
 * back a file that no longer matches the register it claims to describe.
 */
export function useReturnExport() {
  return useMutation<string, unknown, { fy: string; quarter: string }>({
    mutationFn: ({ fy, quarter }) =>
      authedClient.request<string>(
        `/v1/admin/tax/tds/return-export?fy=${fy}&quarter=${quarter}`,
        { parse: 'text' },
      ),
  });
}
